import { useRef, useState, type ReactNode } from "react";
import { Form, Link, useFetcher } from "react-router";
import type { Team, UserList } from "@repo/shared-types";
import { X } from "lucide-react";
import { conversationPath } from "@/components/conversation";
import { Avatar } from "@/components/avatar";
import { FeelAndNote } from "@/components/feel-and-note";
import { SegmentedControl } from "@/components/form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/ui/input-group";
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { DIVISIONS, levelLabel } from "@/lib/results";
import { MAX_GUEST_NAME, MAX_TEAM, roleLabel, teamDivisionLabel, teamNames, teamPath, type TeamPeople } from "@/lib/teams";

/**
 * Teams on the result form and wherever a part done in teams shows (0027):
 * the teammates box a team is formed with, and the caller's own teams on a
 * part. Uncontrolled like every form here: the box keeps only its chips, and
 * writes each as the inputs src/lib/teams.ts reads.
 */

type Chip =
  | { key: number; kind: "member"; username: string }
  | { key: number; kind: "guest"; name: string; division?: string };

type GuestDivision = (typeof DIVISIONS)[number]["value"];

/**
 * One box of chips: "@" suggests the space's members, who are invited and
 * answer on the team's page; a plain name is a guest — somebody without an
 * account — who needs men or women, which the team's division is worked out
 * from. Each chip is an input of the form named by `form`, or of the form
 * around it when that is left out. `initial` is who was chosen already, when
 * a team typed before is opened again.
 */
export function TeamPeopleBox({
  form,
  prefix,
  slug,
  initial,
  heading,
}: {
  form?: string;
  prefix: string;
  slug: string;
  initial?: TeamPeople;
  /**
   * What it is called: in a part's dialog a field among others,
   * "Teammates", when left out; on a team's page nothing (null), as the
   * section it sits in says already.
   */
  heading?: ReactNode;
}) {
  const [chips, setChips] = useState<Chip[]>(() => [
    ...(initial?.invite ?? []).map((username, key): Chip => ({ key, kind: "member", username })),
    ...(initial?.guests ?? []).map(
      (guest, index): Chip => ({ key: (initial?.invite.length ?? 0) + index, kind: "guest", ...guest }),
    ),
  ]);
  const [text, setText] = useState("");
  const [nextKey, setNextKey] = useState(() => (initial?.invite.length ?? 0) + (initial?.guests.length ?? 0));
  const suggestions = useFetcher<UserList>();
  const asked = useRef<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const query = text.trim().startsWith("@") ? text.trim().slice(1) : null;
  const full = chips.length + 1 >= MAX_TEAM;
  const users =
    query !== null && suggestions.data && "users" in suggestions.data
      ? suggestions.data.users.filter(
          (user) => !chips.some((chip) => chip.kind === "member" && chip.username === user.username),
        )
      : [];

  const follow = (typed: string) => {
    setText(typed);
    const at = typed.trim().startsWith("@") ? typed.trim().slice(1) : null;
    if (at !== null && asked.current !== at) {
      asked.current = at;
      suggestions.load(conversationPath(slug, { want: "members", q: at || null }));
    }
  };
  const add = (chip: { kind: "member"; username: string } | { kind: "guest"; name: string }) => {
    if (full) return;
    setChips((all) =>
      chip.kind === "member" && all.some((other) => other.kind === "member" && other.username === chip.username)
        ? all
        : [...all, { ...chip, key: nextKey }],
    );
    setNextKey((key) => key + 1);
    setText("");
    asked.current = null;
    input.current?.focus();
  };
  const addTyped = () => {
    const typed = text.trim();
    if (!typed) return;
    if (typed.startsWith("@")) {
      if (typed.length > 1) add({ kind: "member", username: typed.slice(1) });
    } else {
      add({ kind: "guest", name: typed.slice(0, MAX_GUEST_NAME) });
    }
  };
  const choose = (key: number, division: GuestDivision) =>
    setChips((all) => all.map((chip) => (chip.key === key && chip.kind === "guest" ? { ...chip, division } : chip)));

  let guestIndex = 0;
  return (
    <div className="space-y-3">
      {heading === undefined ? <p className="text-sm font-medium text-foreground">Teammates</p> : heading}
      {chips.length > 0 && (
        <ul aria-label="Teammates" className="flex flex-wrap items-center gap-2">
          {chips.map((chip) => {
            const who = chip.kind === "member" ? `@${chip.username}` : chip.name;
            const remove = (
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={`Remove ${who}`}
                onClick={() => setChips((all) => all.filter((other) => other.key !== chip.key))}
                className="rounded-full text-muted-foreground hover:text-foreground"
              >
                <X />
              </Button>
            );
            if (chip.kind === "member") {
              return (
                <li key={chip.key} className="min-w-0 max-w-full">
                  <Badge variant="secondary" className="h-8 max-w-full gap-1 pr-1 pl-3 text-sm font-normal">
                    <input form={form} type="hidden" name={`${prefix}.team.invite`} value={chip.username} />
                    <span className="truncate">@{chip.username}</span>
                    {remove}
                  </Badge>
                </li>
              );
            }
            const index = guestIndex++;
            const division: GuestDivision | "" =
              chip.division === "men" || chip.division === "women" ? chip.division : "";
            // A guest takes a line of their own: their name, and men or women beside it.
            return (
              <li key={chip.key} className="flex w-full min-w-0 flex-wrap items-center gap-2">
                <Badge variant="outline" className="h-8 max-w-full gap-1 pr-1 pl-3 text-sm font-normal">
                  <input form={form} type="hidden" name={`${prefix}.team.guests.${index}.name`} value={chip.name} />
                  <span className="truncate">{chip.name}</span>
                  {remove}
                </Badge>
                {/* The segments' own input is left unnamed, so it sends nothing:
                    the one after them carries the choice to the form `form`
                    names, as every chip's input does. Their label is for a
                    screen reader; the name is right beside them. */}
                <div className="min-w-40 flex-1 [&_legend]:sr-only">
                  <SegmentedControl<GuestDivision>
                    label={`${chip.name}'s division`}
                    name=""
                    options={DIVISIONS.map((option) => ({ ...option }))}
                    value={division}
                    onChange={(next) => choose(chip.key, next)}
                  />
                </div>
                <input form={form} type="hidden" name={`${prefix}.team.guests.${index}.division`} value={division} />
              </li>
            );
          })}
        </ul>
      )}
      <div className="relative">
        <InputGroup>
          <InputGroupInput
            ref={input}
            value={text}
            disabled={full}
            aria-label="Add a teammate"
            placeholder={full ? `A team has ${MAX_TEAM} people at most` : "@username for a member, or a guest's name"}
            onChange={(change) => follow(change.target.value)}
            onKeyDown={(key) => {
              // Enter adds the chip rather than saving the whole result.
              if (key.key === "Enter") {
                key.preventDefault();
                addTyped();
              }
            }}
          />
          <InputGroupAddon align="inline-end">
            <InputGroupButton variant="secondary" size="sm" onClick={addTyped} disabled={full || !text.trim()}>
              Add
            </InputGroupButton>
          </InputGroupAddon>
        </InputGroup>
        {users.length > 0 && (
          <ul
            aria-label="Members to invite"
            className="absolute top-full left-0 z-20 mt-1 w-64 max-w-full rounded-2xl bg-popover p-1 text-popover-foreground shadow-lg ring-1 ring-foreground/5 dark:ring-foreground/10"
          >
            {users.map((user) => (
              <li key={user.id}>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full justify-start rounded-xl font-normal"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => add({ kind: "member", username: user.username })}
                >
                  <Avatar person={user} size="xs" />
                  <span className="truncate">@{user.username}</span>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        Members are invited, and answer on the team's page. A guest has no account: say whether they are in
        men or women, and the team's division follows everyone on it.
      </p>
    </div>
  );
}

/**
 * Where a team stands with its space: hidden, waiting for verification —
 * only on its event's pages, where the api reads the deadline — verified, or
 * changed since it was.
 */
export function TeamStanding({ team, spaceName }: { team: Team; spaceName: string }) {
  const verification = team.verification;
  return (
    <>
      {team.hiddenAt && (
        <p className="text-sm text-danger">
          Hidden by {spaceName}: taken off this part's leaderboard until a moderator puts it back.
        </p>
      )}
      {team.standing === "waiting" && (
        <p className="text-sm text-warning">
          Waiting for verification: it changed, or went on the board, after the deadline, so it stays off the
          leaderboard until the box verifies it.
        </p>
      )}
      {verification?.current && <p className="text-sm text-success">✓ Verified by {spaceName}</p>}
      {verification && !verification.current && (
        <p className="text-sm text-muted-foreground">
          Changed after it was verified by {spaceName}: the verification was of{" "}
          {verification.scores.map((score) => score.display).filter(Boolean).join(", then ")}
          {verification.level ? ` · ${levelLabel(verification.level)}` : ""}.
        </p>
      )}
      {!verification && team.standing === "unverified" && <p className="text-sm text-muted-foreground">Not verified yet.</p>}
    </>
  );
}

/** A team's score as its people read it: "14:40 · Rx", tiebreaks after. */
export function teamScore(team: Team): string {
  if (!team.recorded) return "No score yet";
  const [first, ...tiebreaks] = team.scores;
  const shown = [first?.display ?? ""];
  for (const tiebreak of tiebreaks) if (tiebreak.display) shown.push(`then ${tiebreak.display}`);
  if (team.level) shown.push(levelLabel(team.level, team.rxPlus));
  return shown.join(" · ");
}

/** Who is on a team, as its line names them, and how many more are invited. */
function TeamPeopleLine({ team }: { team: Team }) {
  const invited = team.members.filter((member) => member.status === "invited").length;
  return (
    <>
      {teamNames({
        members: team.members.filter((member) => member.status !== "invited"),
        people: team.people - invited,
      })}
      {invited > 0 && <> · {invited} invited</>}
    </>
  );
}

/**
 * The caller's own teams on one part — on it, or invited to it — each with
 * its score, its people and a link to its page, where it is answered,
 * changed and talked about.
 */
export function TeamSummaries({ teams, base, eventId }: { teams: Team[]; base: string; eventId: string }) {
  if (teams.length === 0) return null;
  return (
    <ul aria-label="Your teams" className="space-y-2">
      {teams.map((team) => (
        <li key={team.id}>
          <Item variant="muted" size="sm">
            <ItemContent className="min-w-0 gap-0.5">
              <ItemTitle className="line-clamp-none block w-full wrap-anywhere">
                {teamScore(team)}
                {team.division && (
                  <span className="font-normal text-muted-foreground"> · {teamDivisionLabel(team.division)}</span>
                )}
              </ItemTitle>
              <ItemDescription className="wrap-anywhere">
                <TeamPeopleLine team={team} />
              </ItemDescription>
              <ItemDescription className="text-xs">{roleLabel(team.role)}</ItemDescription>
            </ItemContent>
            <ItemActions>
              <Button asChild variant="outline" size="sm">
                <Link to={teamPath(base, eventId, team.id)}>{team.role === "invited" ? "Answer" : "Open"}</Link>
              </Button>
            </ItemActions>
          </Item>
        </li>
      ))}
    </ul>
  );
}

/**
 * The teams an athlete is on through one part, as their log reads them: each
 * team's score, its people, where it stands with its space, the athlete's own
 * note — editable here, as the log is where a result is kept — and the way
 * back to the team's page, while its space is still theirs to open.
 */
export function LogTeams({
  teams,
  meId,
  spaceName,
  reachable,
  busy,
}: {
  teams: Team[];
  meId: string;
  spaceName: string | null;
  /** Whether the space it came from is still the athlete's to open. */
  reachable: boolean;
  busy: boolean;
}) {
  if (teams.length === 0) {
    return <p className="mt-3 border-t border-border pt-3 text-sm text-muted-foreground">Done in teams — you're on none.</p>;
  }
  return (
    <ul aria-label="Your teams" className="mt-3 space-y-2 border-t border-border pt-3">
      {teams.map((team) => {
        const me = team.members.find((member) => member.user?.id === meId);
        const openIn = reachable && team.spaceSlug && team.eventId && (
          <Button asChild variant="outline" size="sm">
            <Link to={teamPath(`/t/${team.spaceSlug}`, team.eventId, team.id)}>Open in {spaceName ?? "its space"}</Link>
          </Button>
        );
        return (
          <li key={team.id} className="space-y-1.5 rounded-2xl bg-muted/50 p-3 text-sm">
            <p className="wrap-anywhere">
              <span className="font-medium text-foreground">{teamScore(team)}</span>
              {team.division && <span className="text-muted-foreground"> · {teamDivisionLabel(team.division)}</span>}
            </p>
            <p className="text-muted-foreground wrap-anywhere">
              <TeamPeopleLine team={team} />
            </p>
            <TeamStanding team={team} spaceName={spaceName ?? "its space"} />
            {me && (
              <Form method="post" className="space-y-3 pt-1" aria-label="Your note on this team">
                <input type="hidden" name="intent" value="team-note" />
                <input type="hidden" name="team" value={team.id} />
                <FeelAndNote feelName="feel" noteName="note" feelLabel="Feel" feel={me.feel} note={me.note} />
                {/* Its way back sits beside Save, so the card ends in one row of buttons. */}
                <div className="flex flex-wrap gap-2">
                  <Button type="submit" variant="outline" size="sm" disabled={busy}>
                    Save
                  </Button>
                  {openIn}
                </div>
              </Form>
            )}
            {!me && openIn}
          </li>
        );
      })}
    </ul>
  );
}
